import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  Chip,
  LinearProgress,
} from "@mui/material";
import PlaylistPlayIcon from "@mui/icons-material/PlaylistPlay";
import ReportProblemOutlinedIcon from "@mui/icons-material/ReportProblemOutlined";
import HandshakeOutlinedIcon from "@mui/icons-material/HandshakeOutlined";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BuildIcon from "@mui/icons-material/Build";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import IconButton from "@mui/material/IconButton";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import PanToolIcon from "@mui/icons-material/PanTool";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";
import {
  Task,
  TaskStatus,
  TaskDetails,
  HumanSupport,
  workflowEventDictionary,
} from "../../types/tasks";
import TaskExecutionItem from "./TaskExecution";
import { getRelativeTime } from "../../utils/dateutils";
import { getLocalizedTitle } from "../../utils/taskutils";
import ModernHelpField from "../taskBoard/HelpFieldJess";
import FeedbackField from "./FeedbackField";
import TakeOverField from "../taskBoard/TakeOverField";
import { TaskBoardWebSocketConnection } from "../../services/taskBoard";
import { UseChatJessWebSocketReturn } from "../../hooks/ChatJess/useChatJessWebSocket";
import logger from "../../utils/logger";

type Props = {
  task: Task;
  details?: TaskDetails;
  open: boolean;
  socketConnection: TaskBoardWebSocketConnection | null;
  chatJessWebSocket?: UseChatJessWebSocketReturn;
  onClose: () => void;
  onTakeOver: (taskId: string, reason: string) => void;
  onGiveFeedback?: (task: Task, stepId: string, feedback: string) => void;
  onSendHelp?: (taskId: string, answer: string) => void;
};

const TaskDetailsModal: React.FC<Props> = ({
  task,
  details,
  open,
  onClose,
  onTakeOver,
  onGiveFeedback,
  onSendHelp,
}) => {
  const { t } = useTranslation();
  const [takeOverReason, setTakeOverReason] = useState("");
  const [helpReply, setHelpReply] = useState("");
  const [helpReplySent, setHelpReplySent] = useState(false);
  const [reviewSent, setReviewSent] = useState(false);
  const [showTakeOverInput, setShowTakeOverInput] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [activeExecutionStep, setActiveExecutionStep] = useState<number | null>(
    null,
  );

  const [showFeedbackInput, setShowFeedbackInput] = useState(false);
  const [showPendingAlert, setShowPendingAlert] = useState(false);
  const [actionsMinimized, setActionsMinimized] = useState(false);

  const [typedLines, setTypedLines] = useState<string[]>([]);
  const [currentLineIndex, setCurrentLineIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");

  const display: TaskDetails = details ?? {
    ...task,
    planning: task.planning || {
      summary: "",
      text: [],
      requiresFeedback: false,
      workflowCategory: "",
    },
    steps: task.steps || [],
    thoughts: task.thoughts || [],
    validations: task.validations || [],
    statusLabel: "",
    aiAgentName: task.aiAgentName || "",
    allowTakeOver: task.allowTakeOver ?? true,
    requiresHumanAnswer: task.requiresHumanAnswer || false,
    requiresHumanAction: task.requiresHumanAction || false,
  };

  const time = getRelativeTime(display.createdAt, t);

  let firstNotAnsweredSupport: HumanSupport | undefined;

  for (const step of display.steps) {
    if (!step.humanSupport) {
      continue;
    }
    for (const support of step.humanSupport) {
      if (
        !support.isCompleted &&
        support.humanSupportMessage?.[0]?.question &&
        !support.humanSupportMessage?.[0]?.answer
      ) {
        firstNotAnsweredSupport = support;
        break;
      }
    }
    if (firstNotAnsweredSupport) {
      break;
    }
  }

  const totalActions =
    (display.requiresHumanAnswer && !helpReplySent ? 1 : 0) +
    (display.requiresHumanAction && !reviewSent ? 1 : 0);
  const completedActions =
    (display.requiresHumanAnswer && helpReplySent ? 1 : 0) +
    (display.requiresHumanAction && reviewSent ? 1 : 0);
  const progressPercentage =
    totalActions > 0 ? (completedActions / totalActions) * 100 : 0;

  const isAllCompleted = () => {
    const helpDone = !display.requiresHumanAnswer || helpReplySent;
    const reviewDone = !display.requiresHumanAction || reviewSent;
    return helpDone && reviewDone;
  };

  useEffect(() => {
    if (open) {
      const answer =
        firstNotAnsweredSupport?.humanSupportMessage?.[0]?.answer || "";

      setHelpReplySent(!!answer);
      setHelpReply(answer);
      setReviewSent(display.reviewed || false);
    }
  }, [open]);

  useEffect(() => {
    if (display.status === "doing") {
      setActiveExecutionStep(0);
    } else {
      setActiveExecutionStep(null);
    }
  }, [display.status, display.steps]);

  useEffect(() => {
    if (!open || display.status !== "todo") return;

    setShowPendingAlert(false);
    setTypedLines([]);
    setCurrentText("");
    setCurrentLineIndex(0);
  }, [open, display.status]);

  useEffect(() => {
    if (isAllCompleted()) setShowPendingAlert(false);
  }, [reviewSent, helpReplySent]);

  useEffect(() => {
    if (
      display.status !== "todo" ||
      !display.planning?.text ||
      display.planning.text.length === 0 ||
      currentLineIndex >= display.planning.text.length
    )
      return;

    let charIndex = 0;
    const line = display.planning.text[currentLineIndex];
    setCurrentText("");

    const interval = setInterval(() => {
      setCurrentText((prev) => prev + line[charIndex]);
      charIndex++;

      if (charIndex === line.length) {
        clearInterval(interval);
        setTypedLines((prev) => [...prev, line]);
        setTimeout(() => {
          if (currentLineIndex < (display.planning?.text?.length || 0) - 1) {
            setCurrentLineIndex((prev) => prev + 1);
          }
        }, 800);
      }
    }, 50);

    return () => clearInterval(interval);
  }, [currentLineIndex, display.planning?.text, display.status]);

  const handleStepCompletion = (completedStepIndex: number) => {
    if (display.steps && display.steps[completedStepIndex]) {
      display.steps[completedStepIndex].isCompleted = true;
    }

    if (completedStepIndex < (display.steps || []).length - 1) {
      setActiveExecutionStep(completedStepIndex + 1);
    } else {
      setActiveExecutionStep(null);
    }
  };

  const resetAll = () => {
    setTakeOverReason("");
    setShowTakeOverInput(false);
    setShowWarning(false);
    setShowPendingAlert(false);
  };

  const handleGiveFeedbackClick = async (feedbackText: string) => {
    if (!feedbackText.trim()) return;

    try {
      onGiveFeedback?.(
        display,
        firstNotAnsweredSupport!.stepId!,
        feedbackText.trim(),
      );

      setReviewSent(true);
      setShowFeedbackInput(false);

      if (
        display.status === "feedback" &&
        display.requiresHumanAnswer &&
        display.requiresHumanAction
      ) {
        if (!helpReplySent) setShowPendingAlert(true);
      }
    } catch (err) {
      logger.error("Erro ao enviar feedback:", err);
    }
  };

  const handleSendHelpClick = async (answer: string) => {
    if (!answer.trim()) return;

    try {
      onSendHelp?.(display.id, answer.trim());

      setHelpReplySent(true);

      if (
        display.status === "feedback" &&
        display.requiresHumanAnswer &&
        display.requiresHumanAction &&
        !reviewSent
      ) {
        setShowPendingAlert(true);
      }
    } catch (err) {
      logger.error("Error sending help:", err);
    }
  };

  const handleClose = () => {
    if (display.status === "feedback") {
      const bothStatusesPresent =
        display.requiresHumanAnswer && display.requiresHumanAction;
      const onlyOneAnswered =
        (helpReplySent && !reviewSent) || (!helpReplySent && reviewSent);

      if (bothStatusesPresent && onlyOneAnswered) {
        setShowPendingAlert(true);
        return;
      }
      resetAll();
      onClose();
    } else {
      resetAll();
      onClose();
    }
  };

  const navigateToAction = (actionType: "feedback" | "help") => {
    if (actionType === "feedback") {
      setShowFeedbackInput(true);
      setTimeout(() => {
        const feedbackElement = document.getElementById("feedback-input");
        if (feedbackElement) {
          feedbackElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);
    } else if (actionType === "help") {
      setTimeout(() => {
        const helpElement = document.getElementById("help-input");
        if (helpElement) {
          helpElement.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 100);
    }
    setActionsMinimized(false);
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "1em",
          boxShadow: "0 0.5em 1.5em rgba(0,0,0,0.12)",
        },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          flexDirection: "column",
          px: "1.5em",
          pt: "1.25em",
          pb: "1.75em",
        }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            width: "100%",
          }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700}>
              {getLocalizedTitle(display.title, i18n.language)}
            </Typography>

            {/* Chips  */}
            {display.eventType && (
              <Box
                sx={{
                  mt: "0.25em",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "0.25em",
                  backgroundColor: "#DBEAFE",
                  color: "#1E40AF",
                  px: "0.5em",
                  py: "0.25em",
                  borderRadius: "0.375em",
                  fontWeight: 600,
                  fontSize: "0.7em",
                  width: "fit-content",
                }}
              >
                <PersonOutlineIcon sx={{ fontSize: "1em" }} />
                {t(
                  `workflowEventFull.${workflowEventDictionary[display.eventType]}`,
                )}
              </Box>
            )}

            {/* relative time */}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: "0.25em", display: "block" }}
            >
              {time}
            </Typography>
          </Box>

          <Box sx={{ display: "flex", gap: "0.5em" }}>
            {display.status === "feedback" ? (
              <>
                {display.requiresHumanAnswer && (
                  <Chip
                    size="small"
                    icon={<ReportProblemOutlinedIcon fontSize="small" />}
                    label={t("needHelp")}
                    color="warning"
                    sx={{ fontWeight: 600 }}
                  />
                )}
                {display.requiresHumanAction && (
                  <Chip
                    size="small"
                    icon={<WarningAmberIcon fontSize="small" />}
                    label={t("needsReview")}
                    color="error"
                    sx={{ fontWeight: 600 }}
                  />
                )}
              </>
            ) : (
              <Chip
                size="small"
                label={t(`taskStatus.${display.status}`)}
                icon={
                  {
                    todo: <PlaylistPlayIcon />,
                    doing: <BuildIcon />,
                    feedback: <ReportProblemOutlinedIcon />,
                    done: <HandshakeOutlinedIcon />,
                  }[display.status as TaskStatus]
                }
                sx={{
                  fontWeight: 600,
                  fontSize: "0.75em",
                  borderRadius: "0.375em",
                  px: "0.25em",
                  py: "0.25em",
                  height: "auto",
                  bgcolor: {
                    todo: "#E0E7FF",
                    doing: "#DBEAFE",
                    feedback: "#FECACA",
                    done: "#D1FAE5",
                  }[display.status as TaskStatus],
                  color: {
                    todo: "#4338CA",
                    doing: "#2563EB",
                    feedback: "#B91C1C",
                    done: "#065F46",
                  }[display.status as TaskStatus],
                  "& .MuiChip-icon": {
                    color: "inherit",
                    fontSize: "1.125em",
                    ml: 0,
                    mr: "0.25em",
                  },
                }}
              />
            )}
          </Box>
        </Box>
      </DialogTitle>

      {/* take over */}
      {display.allowTakeOver &&
        display.status !== "done" &&
        showTakeOverInput && (
          <Box sx={{ mt: "1.5em", px: "1.5em" }}>
            <TakeOverField
              onSubmit={(reason) => {
                onTakeOver(display.id, reason);
                onClose();
              }}
              onCancel={() => setShowTakeOverInput(false)}
            />
          </Box>
        )}

      {/* pending alert */}
      {showPendingAlert && (
        <Box
          sx={{
            mb: "1em",
            px: "1.5em",
            pt: "0.5em",
            animation: "fadeIn 0.3s ease-in-out",
            "@keyframes fadeIn": {
              "0%": { opacity: 0, transform: "translateY(-0.625em)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              background: "linear-gradient(to right, #fffaf0, #fff4e5)",
              borderRadius: "0.625em",
              overflow: "hidden",
              position: "relative",
              border: "0.0625em solid #e0e7ff",
              boxShadow: "0 0.125em 0.5em rgba(0,0,0,0.05)",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                left: 0,
                top: 0,
                bottom: 0,
                width: "0.25em",
                backgroundColor: "#f59e0b",
                borderTopLeftRadius: "0.625em",
                borderBottomLeftRadius: "0.625em",
              }}
            />

            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: "1em",
                pb: actionsMinimized ? "1em" : "0.5em",
                borderBottom: actionsMinimized ? "none" : "1px solid #e5e7eb",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: "0.5em" }}>
                <WarningAmberIcon sx={{ color: "#f59e0b" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  color="#92400e"
                >
                  {totalActions === 1
                    ? t("pendingAction")
                    : t("pendingActions", { count: totalActions })}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => setActionsMinimized(!actionsMinimized)}
                sx={{ color: "#92400e" }}
              >
                {actionsMinimized ? (
                  <ArrowForwardIcon sx={{ transform: "rotate(90deg)" }} />
                ) : (
                  <ArrowForwardIcon sx={{ transform: "rotate(-90deg)" }} />
                )}
              </IconButton>
            </Box>

            {!actionsMinimized && (
              <>
                <LinearProgress
                  variant="determinate"
                  value={progressPercentage}
                  sx={{
                    height: "0.25em",
                    backgroundColor: "#fff7ed",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "#f59e0b",
                    },
                  }}
                />

                <Box sx={{ p: "1em" }}>
                  <Typography
                    variant="caption"
                    sx={{ display: "block", mb: "0.9375em", color: "#a16207" }}
                  >
                    <CheckCircleOutlineIcon
                      sx={{
                        fontSize: "0.875em",
                        mr: "0.25em",
                        verticalAlign: "text-bottom",
                      }}
                    />
                    {t("completedActionsCount", {
                      completed: completedActions,
                      total: totalActions,
                    })}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "0.5em",
                    }}
                  >
                    {!helpReplySent && display.requiresHumanAnswer && (
                      <Button
                        variant="outlined"
                        onClick={() => navigateToAction("help")}
                        startIcon={
                          <ReportProblemOutlinedIcon
                            sx={{ color: "#f59e0b" }}
                          />
                        }
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          borderColor: "#fef3c7",
                          backgroundColor: "#fffbeb",
                          color: "#92400e",
                          "&:hover": {
                            backgroundColor: "#fef3c7",
                            borderColor: "#f59e0b",
                          },
                          py: "0.5em",
                          fontWeight: 500,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {t("answerJessQuestion")}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mt: "0.25em",
                            }}
                          >
                            <AccessTimeIcon
                              sx={{
                                fontSize: "0.875em",
                                mr: "0.25em",
                                color: "#d97706",
                              }}
                            />
                            <Typography variant="caption" color="#d97706">
                              {t("estimatedTimeOneMinute")}
                            </Typography>
                          </Box>
                        </Box>
                      </Button>
                    )}

                    {!reviewSent && display.requiresHumanAction && (
                      <Button
                        variant="outlined"
                        onClick={() => navigateToAction("feedback")}
                        startIcon={
                          <ChatBubbleOutlineIcon sx={{ color: "#ef4444" }} />
                        }
                        sx={{
                          justifyContent: "flex-start",
                          textTransform: "none",
                          borderColor: "#fee2e2",
                          backgroundColor: "#fef2f2",
                          color: "#b91c1c",
                          "&:hover": {
                            backgroundColor: "#fee2e2",
                            borderColor: "#ef4444",
                          },
                          py: "0.5em",
                          fontWeight: 500,
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                        >
                          <Typography variant="body2" fontWeight={600}>
                            {t("giveTaskFeedback")}
                          </Typography>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              mt: "0.25em",
                            }}
                          >
                            <AccessTimeIcon
                              sx={{
                                fontSize: "0.875em",
                                mr: "0.25em",
                                color: "#dc2626",
                              }}
                            />
                            <Typography variant="caption" color="#dc2626">
                              {t("estimatedTimeTwoMinutes")}
                            </Typography>
                          </Box>
                        </Box>
                      </Button>
                    )}
                  </Box>
                </Box>
              </>
            )}
          </Paper>
        </Box>
      )}

      <DialogContent
        sx={{
          px: "1.5em",
          py: "1em",
          borderTop: "1px solid #eee",
          borderBottom: "1px solid #eee",
        }}
      >
        <Box sx={{ height: "1em" }} />

        {/* Planejamento */}
        <Paper
          elevation={0}
          sx={{
            p: "1.25em",
            mb: "1.25em",
            borderRadius: "0.5em",
            backgroundColor: "#f8f9fa",
            position: "relative",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "0.9375em",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <PlaylistPlayIcon
                sx={{
                  color: "primary.main",
                  mr: "0.75em",
                  fontSize: "1.4em",
                }}
              />
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="text.primary"
              >
                {t("planning")}
              </Typography>
            </Box>

            <Button
              size="small"
              onClick={() => setShowFeedbackInput(true)}
              startIcon={<ChatBubbleOutlineIcon />}
              sx={{
                fontSize: "0.7em",
                textTransform: "none",
                px: "0.625em",
                py: "0.125em",
                borderColor: "#f59e0b",
                backgroundColor: "#fff8e1",
                color: "#92400e",
                "&:hover": {
                  borderColor: "#d97706",
                  backgroundColor: "#ffecb3",
                },
                flexShrink: 0,
                whiteSpace: "nowrap",
                maxWidth: "100%",
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                "@media (max-width: 420px)": {
                  fontSize: "0.6em",
                  px: "0.375em",
                  ".MuiButton-startIcon": {
                    marginRight: "0.25em",
                  },
                },
              }}
            >
              {t("giveFeedback")}
              {display.requiresHumanAction && " *"}
            </Button>
          </Box>

          <Box component="ul" sx={{ pl: "1.25em", m: 0, listStyle: "none" }}>
            {(display.status === "todo"
              ? typedLines
              : display.planning.text
            ).map((line, idx) => (
              <Box
                component="li"
                key={idx}
                sx={{
                  mb: "0.375em",
                  display: "flex",
                  alignItems: "flex-start",
                }}
              >
                <Typography sx={{ mr: "0.5em", color: "primary.main" }}>
                  •
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {line}
                </Typography>
              </Box>
            ))}
            {display.status === "todo" &&
              currentLineIndex < display.planning.text.length &&
              currentText !== display.planning.text[currentLineIndex] && (
                <Box
                  component="li"
                  key="typing"
                  sx={{
                    mb: "0.375em",
                    display: "flex",
                    alignItems: "flex-start",
                  }}
                >
                  <Typography sx={{ mr: "0.5em", color: "primary.main" }}>
                    •
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {currentText}
                  </Typography>
                </Box>
              )}
          </Box>

          {showFeedbackInput && !reviewSent && (
            <Box id="feedback-input" sx={{ mt: "1.5em" }}>
              <FeedbackField
                onSubmit={handleGiveFeedbackClick}
                onClose={() => setShowFeedbackInput(false)}
                task={display}
              />
            </Box>
          )}
        </Paper>

        {/* help field answer */}
        {display.status === "feedback" && display.requiresHumanAnswer && (
          <Box id="help-input" sx={{ mb: "1.5em" }}>
            <ModernHelpField
              question={
                firstNotAnsweredSupport?.humanSupportMessage?.[0]?.question ||
                t("defaultHelpQuestion")
              }
              helpReply={helpReply}
              setHelpReply={setHelpReply}
              helpReplySent={helpReplySent}
              onSendHelp={handleSendHelpClick}
            />
          </Box>
        )}

        {/* Execution */}
        {(display.steps || []).length > 0 && display.status !== "todo" && (
          <Paper
            elevation={0}
            sx={{
              p: "1.25em",
              mb: "1.25em",
              borderRadius: "0.5em",
              backgroundColor: "#f8f9fa",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", mb: "0.75em" }}>
              <BuildIcon
                sx={{ color: "primary.main", mr: "0.75em", fontSize: "1.4em" }}
              />
              <Typography
                variant="subtitle1"
                fontWeight={600}
                color="text.primary"
              >
                {t("execution")}
              </Typography>
            </Box>

            <Box sx={{ pl: "1.25em" }}>
              {display.steps.map((step, index) => {
                const isActive =
                  display.status === "doing" && index === activeExecutionStep;
                const isComplete = display.status === "done";

                return (
                  <TaskExecutionItem
                    key={index}
                    item={step}
                    index={index}
                    isActive={isActive}
                    isComplete={isComplete}
                    onComplete={() => handleStepCompletion(index)}
                  />
                );
              })}
            </Box>
          </Paper>
        )}
      </DialogContent>

      <DialogActions
        sx={{
          px: "1.5em",
          py: "1em",
          display: "flex",
          justifyContent: "flex-end",
          gap: "0.5em",
        }}
      >
        {display.allowTakeOver &&
          display.status !== "done" &&
          !showTakeOverInput &&
          !showWarning && (
            <Button
              variant="contained"
              onClick={() => setShowTakeOverInput(true)}
              startIcon={<PanToolIcon />}
              sx={{
                textTransform: "none",
                borderRadius: "0.5em",
                fontWeight: 600,
                py: "0.5em",
                px: "1em",
                background: "linear-gradient(90deg, #6366f1 0%, #8b5cf6 100%)",
                color: "white",
                "&:hover": {
                  background:
                    "linear-gradient(90deg, #4f46e5 0%, #7c3aed 100%)",
                },
              }}
            >
              {t("assumir")}
            </Button>
          )}

        <Button
          onClick={handleClose}
          variant="outlined"
          sx={{
            textTransform: "none",
            borderRadius: "0.5em",
            fontWeight: 600,
          }}
        >
          {t("close")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TaskDetailsModal;
